import type OpenAI from 'openai'

import type { ResolvedPageContext } from './pipeline/page-context'
import type { ConfirmedEntity } from './types'

export function buildInitialMessages(
  prevMessages: OpenAI.ChatCompletionMessageParam[],
  systemPrompt: string,
): OpenAI.ChatCompletionMessageParam[] {
  const stripped = stripToolRounds(prevMessages)
  if (stripped.some((m) => m.role === 'user')) {
    return stripped
  }
  return [{ role: 'system', content: systemPrompt }, ...stripped]
}

/**
 * Returns true for messages that are per-turn context injections — they are
 * always rebuilt fresh from live state, so stale copies must be stripped before
 * each API call and must never appear in the UI.
 *
 * Matches by content prefix so the base system prompt (which contains rule text
 * that *mentions* these tags) is never accidentally stripped. Add new injection
 * types here when `maybeXxxMessage` helpers are introduced.
 *
 * `<live_page_context>` has no live producer (its helper was removed), but the
 * check stays: conversations persisted before storeMessages() stripped injections
 * may still hold one, and must be stripped on resume rather than leak into the model.
 */
export function isPerTurnInjection(
  m: OpenAI.ChatCompletionMessageParam,
): boolean {
  if (typeof m.content !== 'string') return false
  return (
    m.content.startsWith('<page_context') ||
    m.content.startsWith('<confirmed_entities>') ||
    m.content.startsWith('<live_page_context>')
  )
}

/**
 * Remove all tool call rounds from history before sending to the model.
 * Tool results are always re-fetched fresh — keeping old raw data wastes tokens,
 * causes stale-data confusion, and inflates context with no benefit.
 * What remains: user messages + assistant text responses (already contain rendered summaries).
 */
export function stripToolRounds(
  messages: OpenAI.ChatCompletionMessageParam[],
): OpenAI.ChatCompletionMessageParam[] {
  const result: OpenAI.ChatCompletionMessageParam[] = []
  for (const m of messages) {
    if (m.role === 'tool') continue
    if (isPerTurnInjection(m)) continue
    if (m.role === 'assistant') {
      // Drop tool_calls — paired tool results are stripped, so sending tool_calls
      // without results would break the API. Also handles SDK bug (tool_calls: null).
      if (!m.tool_calls?.length) {
        result.push({ role: 'assistant', content: m.content ?? null })
        continue
      }
      // tool_calls present: keep only if there's visible text alongside them.
      // tool_calls-only messages (content null) are internal scaffolding — drop.
      if (m.content) {
        result.push({ role: 'assistant', content: m.content })
      }
      continue
    }
    result.push(m)
  }
  return result
}

export function getLastAssistantText(
  messages: OpenAI.ChatCompletionMessageParam[],
): string | undefined {
  const last = [...messages].reverse().find((m) => m.role === 'assistant')
  return typeof last?.content === 'string' ? last.content : undefined
}

function escapeXmlAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function maybePageContextMessage(
  ctx: ResolvedPageContext,
): OpenAI.ChatCompletionMessageParam[] {
  const parts: string[] = [`mode="${escapeXmlAttr(ctx.mode)}"`]
  if (ctx.dataClass) {
    parts.push(`dataClass="${escapeXmlAttr(ctx.dataClass)}"`)
  }
  if (ctx.id) {
    parts.push(`id="${escapeXmlAttr(ctx.id)}"`)
  }
  const tab = ctx.urlQuery?.tab
  if (tab) {
    parts.push(`tab="${escapeXmlAttr(tab)}"`)
  }
  // Emitted as 'user' (not 'system') so the gateway does not collect it into the
  // Bedrock system array. The cache point sits at the end of that array; a changing
  // page_context inside it would invalidate the cached prefix on every navigation.
  // stripToolRounds identifies it by content marker rather than role, so it is still
  // stripped from history before the next turn's fresh copy is appended.
  return [{ role: 'user', content: `<page_context ${parts.join(' ')} />` }]
}

export function maybeConfirmedEntitiesMessage(
  entities: readonly ConfirmedEntity[],
): OpenAI.ChatCompletionMessageParam[] {
  if (entities.length === 0) return []
  const inner = entities
    .map((e) => {
      const title = escapeXmlAttr(e.title)
      const url = escapeXmlAttr(e.url)
      return `  <entity dataClass="${escapeXmlAttr(e.dataClass)}" id="${escapeXmlAttr(e.id)}" url="${url}">${title}</entity>`
    })
    .join('\n')
  return [
    {
      role: 'system',
      content: `<confirmed_entities>\n${inner}\n</confirmed_entities>`,
    },
  ]
}
